/**
 * ProjectHeader — "Variant A" project chrome (originally explored in
 * a now-retired mockup app, then ported here as the live implementation).
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │ [Project Name ▾]                  🏢Company 📅Date ℹApp │  Project bar
 *   ├────────────────────────────────────────────────────────┤
 *   │  Overview  TARA Workflow  Architecture  …              │  Line-tab strip
 *   └────────────────────────────────────────────────────────┘
 *
 * Both rows share the mode-invariant `--color-surface-grey` (#D9D9D9).
 * Inter-row separation comes from a `color-mix`-derived darker shade
 * of the surface, so borders track the surface if the token moves.
 *
 * Switcher trigger is hyperlink-styled (mirrors v2 Step 0's ModeSwitch).
 * Clicking opens a dropdown listing the user's other projects (search
 * filter + "View all" footer); selecting one navigates to that
 * project's workspace.
 *
 * Tab-scroll affordances (left / right scroll arrows + scroll-into-view
 * on active-tab change) carried over verbatim from the retired
 * `PageHeader.projectNavBar` implementation.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listSessions } from '../../../../api/sessions';
import SubHeaderWithTabs from '../../../../components/common/SubHeaderWithTabs';
import SwitcherPill from '../../../../components/common/SwitcherPill';
import { Icon } from '../../../../components/ui/Icon';
import { Input } from '../../../../components/ui/Input';
import { Button } from '../../../../components/ui/Button2';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { useWorkspaceChrome } from '../../WorkspaceChromeContext';
import styles from './ProjectHeader.module.css';

const formatAttackApproach = (raw) => {
  if (raw === 'ATTACK_POTENTIAL') return 'Attack Potential';
  if (raw === 'CAL') return 'CAL Method';
  return raw;
};

const formatCompanyType = (project) => {
  const type = project?.company_type;
  if (!type) return null;
  if (type === 'Other') return project?.company_type_other || null;
  return type;
};

const STATUS_LABEL = {
  COMPLETED: 'Completed',
  IN_PROGRESS: 'In Progress',
  PAUSED: 'In Progress',
  NEW: 'New',
  FAILED: 'Failed',
};

const projectStatusLabel = (status) =>
  STATUS_LABEL[status] ?? (status ? status : 'Unknown');

export default function ProjectHeader({
  project,
  navItems = [],
  activeNavItem = null,
  onNavigation,
}) {
  const navigate = useNavigate();

  // Controls the active tab teleports into the subheader's row-2 ends
  // (e.g. the TARA workflow rail/panel toggles). Empty for tabs that
  // register nothing — the ends simply render no control.
  const { lead, trail } = useWorkspaceChrome();

  // ── Switcher dropdown ────────────────────────────────────────
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switcherLoading, setSwitcherLoading] = useState(false);
  const [switcherError, setSwitcherError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  // Lazy-load the sessions list on first open. Subsequent opens reuse
  // the cached payload; if the cache feels stale a hard refresh on
  // the page brings the list up to date — acceptable for v1.
  useEffect(() => {
    if (!switcherOpen) return;
    if (sessions.length > 0) return;
    let cancelled = false;
    setSwitcherLoading(true);
    setSwitcherError(null);
    listSessions()
      .then((data) => {
        if (cancelled) return;
        setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setSwitcherError(e?.message ?? 'Failed to load projects');
      })
      .finally(() => {
        if (!cancelled) setSwitcherLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [switcherOpen, sessions.length]);

  // Focus the search input on open for keyboard users.
  useEffect(() => {
    if (switcherOpen) searchRef.current?.focus();
  }, [switcherOpen]);

  const otherSessions = useMemo(() => {
    const currentId = project?.session_id;
    return sessions.filter((s) => s.session_id !== currentId);
  }, [sessions, project?.session_id]);

  const filteredSessions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return otherSessions;
    return otherSessions.filter((s) =>
      (s.analysis_name ?? '').toLowerCase().includes(q),
    );
  }, [otherSessions, search]);

  const handleSelectProject = (sessionId) => {
    setSwitcherOpen(false);
    setSearch('');
    navigate(`/project/${sessionId}`);
  };

  const companyType = formatCompanyType(project);

  // Row 1 — project bar (switcher + meta strip share a single
  // left-aligned cluster; nothing pinned to the right edge). Now
  // fills the SubHeaderWithTabs primitive's `header` slot; only
  // styled bits below are project-specific (switcher pill, switcher
  // menu, meta strip).
  const headerContent = (
    <div className={styles.bar}>
      <SwitcherPill
        label={
          // Shell-first: the header mounts before getSession resolves —
          // show a quiet title placeholder until the project lands.
          project ? project.analysis_name : <Skeleton className={styles.titleSkeleton} aria-hidden="true" />
        }
        labelClassName={styles.projectTitle}
        triggerClassName={styles.switcherTrigger}
        isOpen={switcherOpen}
        onToggle={() => project && setSwitcherOpen((v) => !v)}
        onClose={() => setSwitcherOpen(false)}
        triggerTitle="Switch project"
        dropdownClassName={styles.switcherDropdown}
      >
        <Input
          ref={searchRef}
          type="text"
          className={styles.switcherSearch}
          placeholder="Search projects…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={styles.switcherList}>
          {switcherLoading && (
            <div className={styles.switcherEmpty}>Loading…</div>
          )}
          {switcherError && (
            <div className={styles.switcherEmpty}>{switcherError}</div>
          )}
          {!switcherLoading && !switcherError && filteredSessions.length === 0 && (
            <div className={styles.switcherEmpty}>
              {search ? 'No matching projects.' : 'No other projects yet.'}
            </div>
          )}
          {!switcherLoading && filteredSessions.map((s) => (
            <button
              key={s.session_id}
              type="button"
              role="menuitem"
              className={styles.switcherItem}
              onClick={() => handleSelectProject(s.session_id)}
            >
              <span className={styles.switcherItemName}>{s.analysis_name}</span>
              <span className={styles.switcherItemMeta}>
                {projectStatusLabel(s.session_status)}
              </span>
            </button>
          ))}
        </div>
        <div className={styles.switcherFooter}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={styles.switcherFooterBtn}
            onClick={() => {
              setSwitcherOpen(false);
              navigate('/projects');
            }}
          >
            View all projects
          </Button>
        </div>
      </SwitcherPill>

      <div className={styles.metaStrip}>
        {/* Shell-first: hold the meta strip's line with quiet placeholders
            until the project lands (mirrors the title skeleton). */}
        {!project && (
          <>
            <Skeleton className={`${styles.metaSkeleton} ${styles.metaSkeletonLong}`} aria-hidden="true" />
            <Skeleton className={styles.metaSkeleton} aria-hidden="true" />
            <Skeleton className={`${styles.metaSkeleton} ${styles.metaSkeletonShort}`} aria-hidden="true" />
          </>
        )}
        {project?.company_name && (
          <span className={styles.metaItem}>
            <Icon name="building" size={14} />
            {project?.company_name}
            {companyType && <span className={styles.metaItemSub}>({companyType})</span>}
          </span>
        )}
        {project?.created_at && (
          <span className={styles.metaItem}>
            <Icon name="calendar" size={14} />
            Created {new Date(project?.created_at).toLocaleDateString()}
          </span>
        )}
        {project?.attack_feasibility_approach && (
          <span className={styles.metaItem}>
            <Icon name="info" size={14} />
            {formatAttackApproach(project?.attack_feasibility_approach)}
          </span>
        )}
      </div>
    </div>
  );

  // Map navItems → SubHeaderWithTabs tab config. `title` flows
  // through the primitive to the tab button's `title` attribute.
  const tabsConfig = navItems.map((item) => ({
    id: item.id,
    label: item.label,
    title: item.description,
  }));

  return (
    <SubHeaderWithTabs
      className={styles.root}
      header={headerContent}
      tabs={tabsConfig}
      activeTabId={activeNavItem}
      onTabChange={(id) => onNavigation?.(id)}
      tabVariant="segmented"
      tabsWrapClassName={styles.tabStripWrap}
      tabsStripClassName={styles.tabStrip}
      scrollBtnClassName={styles.scrollBtn}
      leadSlot={lead}
      trailSlot={trail}
    />
  );
}
